/**
 * Core Accordion Component
 *
 * Collapsible content panels with single/multi expand, disabled items,
 * icon support, and bordered/ghost variants. Built on MUI Accordion.
 *
 * @example
 *   <Accordion items={[{ id: '1', title: 'Section 1', content: <p>...</p> }]} />
 *   <Accordion items={items} allowMultiple defaultExpanded={['1']} variant="bordered" />
 */
import React, { useState, useCallback } from 'react';
import {
  Accordion as MuiAccordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
} from '@mui/material';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface AccordionItem {
  id: string;
  title: React.ReactNode;
  /** Subtitle or description shown next to title */
  subtitle?: string;
  /** Panel content */
  content: React.ReactNode;
  /** Icon before title */
  icon?: React.ReactNode;
  /** Disable this panel */
  disabled?: boolean;
  /** Badge/action rendered right-aligned in the header */
  headerAction?: React.ReactNode;
}

export interface AccordionProps {
  items: AccordionItem[];
  /** Allow expanding multiple panels simultaneously */
  allowMultiple?: boolean;
  /** IDs of initially expanded panels */
  defaultExpanded?: string[];
  /** Visual variant */
  variant?: 'default' | 'bordered' | 'ghost';
  /** Size */
  size?: 'sm' | 'md';
  /** Divider between items */
  dividers?: boolean;
  /** Controlled expanded state */
  expanded?: string[];
  /** Controlled change handler */
  onExpandedChange?: (expanded: string[]) => void;
  className?: string;
}

export const Accordion: React.FC<AccordionProps> = ({
  items,
  allowMultiple = false,
  defaultExpanded = [],
  variant = 'default',
  size = 'md',
  dividers = true,
  expanded: controlledExpanded,
  onExpandedChange,
  className = '',
}) => {
  const [internalExpanded, setInternalExpanded] = useState<string[]>(defaultExpanded);
  const expanded = controlledExpanded ?? internalExpanded;
  const setExpanded = onExpandedChange ?? setInternalExpanded;

  const handleToggle = useCallback(
    (id: string) => {
      if (allowMultiple) {
        setExpanded(
          expanded.includes(id) ? expanded.filter((e) => e !== id) : [...expanded, id],
        );
      } else {
        setExpanded(expanded.includes(id) ? [] : [id]);
      }
    },
    [expanded, setExpanded, allowMultiple],
  );

  const variantSx = {
    default: {
      '&::before': { display: 'none' },
      boxShadow: 'none',
      borderRadius: '12px !important',
      mb: dividers ? 1 : 0,
    },
    bordered: {
      '&::before': { display: 'none' },
      boxShadow: 'none',
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: '12px !important',
      mb: 1,
    },
    ghost: {
      '&::before': { display: 'none' },
      boxShadow: 'none',
      bgcolor: 'transparent',
      borderRadius: '8px !important',
      mb: 0,
    },
  };

  return (
    <div className={className}>
      {items.map((item) => (
        <MuiAccordion
          key={item.id}
          expanded={expanded.includes(item.id)}
          onChange={() => handleToggle(item.id)}
          disabled={item.disabled}
          disableGutters
          sx={{
            ...variantSx[variant],
            bgcolor: variant === 'ghost' ? 'transparent' : 'background.paper',
          }}
        >
          <AccordionSummary
            expandIcon={<ChevronDown className="h-4 w-4 text-slate-400" />}
            sx={{
              minHeight: size === 'sm' ? 40 : 52,
              px: size === 'sm' ? 1.5 : 2,
              '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1, my: size === 'sm' ? 0.5 : 1 },
            }}
          >
            {item.icon && <span className="flex items-center text-slate-400 mr-1">{item.icon}</span>}
            <div className="flex-1 min-w-0">
              <Typography variant={size === 'sm' ? 'body2' : 'subtitle2'} fontWeight={600} noWrap>
                {item.title}
              </Typography>
              {item.subtitle && (
                <Typography variant="caption" color="text.secondary" noWrap>
                  {item.subtitle}
                </Typography>
              )}
            </div>
            {item.headerAction && (
              <div onClick={(e) => e.stopPropagation()} className="ml-2">
                {item.headerAction}
              </div>
            )}
          </AccordionSummary>
          <AccordionDetails sx={{ px: size === 'sm' ? 1.5 : 2, pb: size === 'sm' ? 1.5 : 2 }}>
            {item.content}
          </AccordionDetails>
        </MuiAccordion>
      ))}
    </div>
  );
};
export default Accordion;
